import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon, BookOpenIcon, ClockIcon } from '@heroicons/react/24/outline';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import SEO from '@/components/SEO';
import ArticleSchema from '@/components/seo/ArticleSchema';
import PSEODisclaimer from '@/components/seo/PSEODisclaimer';
import { fetchGlossaryEntry } from '@/services/pseoService';

const BASE_URL = 'https://seniorthrive.com';

const LearnPage = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: entry, isLoading, error } = useQuery({
    queryKey: ['glossary-entry', slug],
    queryFn: () => fetchGlossaryEntry(slug!),
    enabled: !!slug,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

  if (isLoading) {
    const termName = slug
      ? slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
      : 'Term';

    return (
      <>
        <SEO
          title={`What Is ${termName}? A Plain-Language Guide`}
          description={`Learn what ${termName} means, why it matters, and what families should know. Written in clear language by the SeniorThrive team.`}
          canonical={`${BASE_URL}/learn/${slug}`}
        />
        <div className="py-20 px-4">
          <div className="container mx-auto max-w-4xl">
            <Skeleton className="h-4 w-24 mb-4" />
            <Skeleton className="h-12 w-full mb-4" />
            <Skeleton className="h-4 w-48 mb-8" />
            <div className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error || !entry) {
    return (
      <div className="py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="text-4xl font-bold text-foreground mb-4">Term Not Found</h1>
          <p className="text-muted-foreground mb-8">
            The term you're looking for doesn't exist or has been moved.
          </p>
          <Button asChild>
            <Link to="/learn">
              <ArrowLeftIcon className="mr-2 w-4 h-4" />
              Back to Learning Center
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const pageUrl = `${BASE_URL}/learn/${entry.slug}`;
  const metaTitle = entry.seo_meta?.metaTitle || `What Is ${entry.term}? A Plain-Language Guide`;
  const metaDescription = entry.seo_meta?.metaDescription || entry.definition;
  const metaKeywords = entry.seo_meta?.keywords?.join(', ') || '';
  const wordCount = [entry.definition, entry.why_it_matters, entry.day_to_day, entry.what_to_do, entry.when_to_get_help].join(' ').split(' ').length;
  const readingTime = Math.ceil(wordCount / 200);

  const categoryLabels: Record<string, string> = {
    clinical: 'Clinical & Assessment',
    'home-safety': 'Home Safety',
    caregiving: 'Caregiving',
    wellness: 'Wellness & Lifestyle',
  };

  const breadcrumbs = [
    { name: 'Home', url: BASE_URL },
    { name: 'Learning Center', url: `${BASE_URL}/learn` },
    { name: entry.term, url: pageUrl },
  ];

  // DefinedTerm schema for Google Knowledge Graph
  const definedTermSchema = {
    '@context': 'https://schema.org',
    '@type': 'DefinedTerm',
    name: entry.term,
    description: entry.definition,
    url: pageUrl,
    inDefinedTermSet: {
      '@type': 'DefinedTermSet',
      name: 'SeniorThrive Learning Center',
      url: `${BASE_URL}/learn`,
    },
  };

  return (
    <>
      <SEO
        title={metaTitle}
        description={metaDescription}
        keywords={metaKeywords}
        url={pageUrl}
        canonical={pageUrl}
        type="article"
        breadcrumbs={breadcrumbs}
        faq={[
          { question: `What is ${entry.term}?`, answer: entry.definition },
          { question: `Why does ${entry.term} matter for families?`, answer: entry.why_it_matters.substring(0, 300) },
        ]}
        article={{
          publishedTime: entry.published_at || entry.created_at,
          modifiedTime: entry.updated_at,
          author: 'SeniorThrive Team',
          section: categoryLabels[entry.category] || entry.category,
        }}
      />
      <ArticleSchema
        title={metaTitle}
        description={metaDescription}
        url={pageUrl}
        authorName="SeniorThrive Team"
        datePublished={entry.published_at || entry.created_at}
        dateModified={entry.updated_at}
      />
      {/* DefinedTerm Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(definedTermSchema) }} />

      <div className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          {/* Back nav */}
          <div className="mb-8">
            <Button variant="ghost" asChild>
              <Link to="/learn">
                <ArrowLeftIcon className="mr-2 w-4 h-4" />
                Learning Center
              </Link>
            </Button>
          </div>

          <article>
            {/* Header */}
            <header className="mb-10">
              {entry.category && (
                <Badge variant="secondary" className="mb-4">
                  {categoryLabels[entry.category] || entry.category}
                </Badge>
              )}
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4 leading-tight">
                What Is {entry.term}?
              </h1>
              <p className="text-xl text-muted-foreground mb-4 leading-relaxed">
                A Plain-Language Guide
              </p>
              <div className="flex items-center text-sm text-muted-foreground gap-6">
                <div className="flex items-center">
                  <BookOpenIcon className="w-4 h-4 mr-2" />
                  <span>SeniorThrive Team</span>
                </div>
                <div className="flex items-center">
                  <ClockIcon className="w-4 h-4 mr-2" />
                  <span>{readingTime} min read</span>
                </div>
              </div>
            </header>

            {/* Definition */}
            <section className="mb-10">
              <div className="bg-blue-50 dark:bg-blue-950/30 rounded-xl p-6 border border-blue-100 dark:border-blue-900">
                <p className="text-lg text-foreground leading-relaxed">{entry.definition}</p>
              </div>
            </section>

            {/* Why It Matters (Sophia angle) */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-foreground mb-4">Why It Matters for Families</h2>
              <p className="text-muted-foreground leading-relaxed text-lg whitespace-pre-line">{entry.why_it_matters}</p>
            </section>

            {/* Day to Day (Nancy angle) */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-foreground mb-4">What It Looks Like Day to Day</h2>
              <p className="text-muted-foreground leading-relaxed text-lg whitespace-pre-line">{entry.day_to_day}</p>
            </section>

            {/* What to Do (Actionable) */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-foreground mb-4">What to Do About It</h2>
              <div className="text-muted-foreground leading-relaxed text-lg whitespace-pre-line">{entry.what_to_do}</div>
            </section>

            {/* When to Get Help (MJ angle) */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-foreground mb-4">When to Get Professional Help</h2>
              <p className="text-muted-foreground leading-relaxed text-lg whitespace-pre-line">{entry.when_to_get_help}</p>
            </section>

            {/* Related Terms */}
            {entry.related_terms.length > 0 && (
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-foreground mb-4">Related Terms</h2>
                <div className="flex flex-wrap gap-2">
                  {entry.related_terms.map((termSlug) => (
                    <Link
                      key={termSlug}
                      to={`/learn/${termSlug}`}
                      className="inline-flex items-center px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm font-medium text-foreground transition-colors"
                    >
                      {termSlug.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Related Checklists */}
            {entry.related_checklist_slugs.length > 0 && (
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-foreground mb-4">Related Checklists</h2>
                <div className="flex flex-wrap gap-2">
                  {entry.related_checklist_slugs.map((checkSlug) => (
                    <Link
                      key={checkSlug}
                      to={`/checklists/${checkSlug}`}
                      className="inline-flex items-center px-4 py-2 rounded-lg bg-green-50 dark:bg-green-950/30 hover:bg-green-100 dark:hover:bg-green-950/50 text-sm font-medium text-green-800 dark:text-green-200 transition-colors border border-green-200 dark:border-green-800"
                    >
                      {checkSlug.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())} Checklist
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Related Blog Articles */}
            {entry.related_blog_slugs.length > 0 && (
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-foreground mb-4">Related Articles</h2>
                <div className="flex flex-col gap-2">
                  {entry.related_blog_slugs.map((blogSlug) => (
                    <Link
                      key={blogSlug}
                      to={`/blog/${blogSlug}`}
                      className="inline-flex items-center px-4 py-3 rounded-lg bg-muted hover:bg-muted/80 text-sm font-medium text-foreground transition-colors"
                    >
                      {blogSlug.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* CTA, feature-aware, drives to free trial */}
            <section className="bg-gradient-to-r from-blue-50 to-teal-50 dark:from-blue-950/30 dark:to-teal-950/30 rounded-xl p-8 text-center border border-blue-100 dark:border-blue-900">
              <h2 className="text-2xl font-bold text-foreground mb-3">
                SeniorThrive Helps With This
              </h2>
              <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                {entry.category === 'clinical'
                  ? 'SeniorThrive turns clinical concepts like this into simple daily tools. Daily Living Skills check-ins, wellness tracking, and care coordination that the whole family can see.'
                  : entry.category === 'home-safety'
                  ? 'SeniorThrive includes a Home Safety Check and room-by-room recommendations built right into the app. See what needs attention and track your progress over time.'
                  : entry.category === 'caregiving'
                  ? 'SeniorThrive gives caregivers and families a shared view of how things are going at home. Daily check-ins, wellness signals, and care coordination that takes tasks off your plate.'
                  : 'SeniorThrive brings safety, wellness, and family connection into one place. Daily check-ins, care coordination, and tools designed for older adults and the people who love them.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button asChild size="lg">
                  <a
                    href={`https://app.seniorthrive.com/auth?signup=true&utm_source=learn&utm_medium=pseo&utm_campaign=${entry.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Start Your Free Trial
                  </a>
                </Button>
                <Button variant="outline" asChild size="lg">
                  <Link to="/features">See All Features</Link>
                </Button>
              </div>
            </section>
          </article>

          {/* Disclaimer, shared across all pSEO pages */}
          <PSEODisclaimer lastUpdated={entry.updated_at} className="mt-12" />

          {/* Footer nav */}
          <div className="border-t pt-8 mt-12">
            <Button asChild>
              <Link to="/learn">
                <ArrowLeftIcon className="mr-2 w-4 h-4" />
                Back to Learning Center
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default LearnPage;
